import React, { useState, useEffect, useRef } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Snackbar,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from "@mui/material";
import {
  Calculate,
  DeleteForever,
  Block,
  LockOpen,
  Save,
  Public,
  Dashboard,
  School,
  People,
  Settings,
  AdminPanelSettings,
  ArrowBack,
  Logout,
  Close as CloseIcon,
  Warning,
  Person,
  Business,
  FileUpload,
  Group,
  HowToReg,
  Assessment,
  Download,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { QRCodeCanvas } from "qrcode.react";
import DeltaLogo from "../../asset/Delta_logo.png";
import { baseUrl, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";

// ============================================
// Modern Dashboard Theme (MUI5)
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

// ============================================
// Main ManagementConsole Component
// ============================================
const ManagementConsole: React.FC = () => {
  const [qrValue, setQrValue] = useState<string>("https://deltamobse.ng/verify/");
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  // Tab State
  const [activeTab, setActiveTab] = useState(0);

  // Global Data States
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [isReleased, setIsReleased] = useState(localStorage.getItem('globalReleaseStatus') === 'true');

  // Snackbar State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  // Count Results Input States
  const [countLgaId, setCountLgaId] = useState("");
  const [countSchoolCode, setCountSchoolCode] = useState("");

  // Checked Results Count State
  const [checkedCount, setCheckedCount] = useState<number | string>(0);
  const [checkedCountLoading, setCheckedCountLoading] = useState(false);

  // Delete Operation Input States
  const [deleteLgaId, setDeleteLgaId] = useState("");
  const [deleteSchoolCode, setDeleteSchoolCode] = useState("");
  const [deleteExamNo, setDeleteExamNo] = useState("");

  // Block/Unblock Operation Input States
  const [blockLgaId, setBlockLgaId] = useState("");
  const [blockSchoolCode, setBlockSchoolCode] = useState("");
  const [blockExamNo, setBlockExamNo] = useState("");

  // Religious Classification Input States
  const [religionUpdateType, setReligionUpdateType] = useState<"student" | "school">("school");
  const [religionLgaId, setReligionLgaId] = useState("");
  const [religionSchoolCode, setReligionSchoolCode] = useState("");
  const [religionExamNo, setReligionExamNo] = useState("");
  const [religionClassification, setReligionClassification] = useState("1");

  // Bulk Download Input States
  const [bulkLgaCode, setBulkLgaCode] = useState("");
  const [bulkSchoolCode, setBulkSchoolCode] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  // Bulk Download LGA Input States
  const [bulkLgaOnlyCode, setBulkLgaOnlyCode] = useState("");
  const [bulkLgaLoading, setBulkLgaLoading] = useState(false);
  const [bulkLgaProgress, setBulkLgaProgress] = useState({ currentSchool: 0, totalSchools: 0, currentStudent: 0, totalStudents: 0 });

  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Get user data from utils
  const user = getUser();

  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    handleCloseConfirmDialog();
  };

  // --- COUNT RESULTS API LOGIC ---
  const handleCountResults = async () => {
    setLoading(true);
    try {
      const payload: any = {};

      if (countLgaId && countLgaId.trim() !== "") {
        payload.lgaCd = countLgaId;
      }

      if (countSchoolCode && countSchoolCode.trim() !== "") {
        payload.institutionCd = countSchoolCode;
      }

      const res = await axios.post(`${baseUrl()}Admin2s/countResults_update`, payload);

      if (res.data.status) {
        setCount(res.data.totalResults);
        showSnackbar(`Total results: ${res.data.totalResults}`, 'success');
      } else {
        showSnackbar(res.data.message || "Failed to count results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // --- GET CHECKED COUNT API LOGIC ---
  const handleGetCheckedCount = async () => {
    setCheckedCountLoading(true);
    try {
      const res = await axios.get(`${baseUrl()}results/getCheckedCount`);
      
      const val = res?.data?.data?.totalChecked ?? res?.data?.count ?? 0;
      if (res?.data?.status) {
        setCheckedCount(val);
        showSnackbar(`Total checked results: ${val}`, 'success');
      } else {
        showSnackbar(res?.data?.message || "Failed to fetch checked count", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setCheckedCountLoading(false);
    }
  };

  // --- DELETE OPERATIONS ---
  const handleDeleteSingleResult = async () => {
    if (!deleteLgaId || !deleteSchoolCode || !deleteExamNo) {
      showSnackbar("Please provide LGA ID, School Code, and Exam Number", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/deleteSingleResult`, {
        lgaId: deleteLgaId,
        schoolCode: deleteSchoolCode,
        examNo: deleteExamNo
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "Student result deleted successfully", 'success');
        // Clear inputs on success
        setDeleteLgaId("");
        setDeleteSchoolCode("");
        setDeleteExamNo("");
      } else {
        showSnackbar(res.data.message || "Failed to delete student result", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchoolResults = async () => {
    if (!deleteLgaId || !deleteSchoolCode) {
      showSnackbar("Please provide LGA ID and School Code", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/deleteResultsBySchool`, {
        lgaId: deleteLgaId,
        schoolCode: deleteSchoolCode
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "School results deleted successfully", 'success');
        setDeleteLgaId("");
        setDeleteSchoolCode("");
      } else {
        showSnackbar(res.data.message || "Failed to delete school results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLGAResults = async () => {
    if (!deleteLgaId) {
      showSnackbar("Please provide LGA ID", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/deleteResultsByLGA`, {
        lgaId: deleteLgaId
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "LGA results deleted successfully", 'success');
        setDeleteLgaId("");
      } else {
        showSnackbar(res.data.message || "Failed to delete LGA results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // --- BLOCK/UNBLOCK OPERATIONS ---
  const handleBlockStudent = async () => {
    if (!blockLgaId || !blockSchoolCode || !blockExamNo) {
      showSnackbar("Please provide LGA ID, School Code, and Exam Number", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/blockStudent`, {
        lgaId: blockLgaId,
        schoolCode: blockSchoolCode,
        examNo: blockExamNo
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "Student result blocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to block student result", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockStudent = async () => {
    if (!blockLgaId || !blockSchoolCode || !blockExamNo) {
      showSnackbar("Please provide LGA ID, School Code, and Exam Number", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/unblockStudent`, {
        lgaId: blockLgaId,
        schoolCode: blockSchoolCode,
        examNo: blockExamNo
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "Student result unblocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to unblock student result", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockSchool = async () => {
    if (!blockLgaId || !blockSchoolCode) {
      showSnackbar("Please provide LGA ID and School Code", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/blockSchool`, {
        lgaId: blockLgaId,
        schoolCode: blockSchoolCode
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "School results blocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to block school results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockSchool = async () => {
    if (!blockLgaId || !blockSchoolCode) {
      showSnackbar("Please provide LGA ID and School Code", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/unblockSchool`, {
        lgaId: blockLgaId,
        schoolCode: blockSchoolCode
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "School results unblocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to unblock school results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockLGA = async () => {
    if (!blockLgaId) {
      showSnackbar("Please provide LGA ID", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/blockLGA`, {
        lgaId: blockLgaId
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "LGA results blocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to block LGA results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockLGA = async () => {
    if (!blockLgaId) {
      showSnackbar("Please provide LGA ID", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/unblockLGA`, {
        lgaId: blockLgaId
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "LGA results unblocked successfully", 'success');
      } else {
        showSnackbar(res.data.message || "Failed to unblock LGA results", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // --- RELIGIOUS CLASSIFICATION OPERATIONS ---
  const handleUpdateReligionForStudent = async () => {
    if (!religionLgaId || !religionSchoolCode || !religionExamNo) {
      showSnackbar("Please provide LGA ID, School Code, and Exam Number", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/updateReligiousForStudent`, {
        lgaId: religionLgaId,
        schoolCode: religionSchoolCode,
        examNo: religionExamNo,
        classification: religionClassification
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "Student religious classification updated successfully", 'success');
        // Clear inputs on success
        setReligionLgaId("");
        setReligionSchoolCode("");
        setReligionExamNo("");
      } else {
        showSnackbar(res.data.message || "Failed to update student religious classification", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReligionForSchool = async () => {
    if (!religionLgaId || !religionSchoolCode) {
      showSnackbar("Please provide LGA ID and School Code", 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl()}Admin2s/updateReligiousForSchool`, {
        lgaId: religionLgaId,
        schoolCode: religionSchoolCode,
        classification: religionClassification
      });

      if (res.data.status) {
        showSnackbar(res.data.message || "School religious classification updated successfully", 'success');
        // Clear inputs on success
        setReligionLgaId("");
        setReligionSchoolCode("");
      } else {
        showSnackbar(res.data.message || "Failed to update school religious classification", 'error');
      }
    } catch (e) {
      showSnackbar("Error connecting to server", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReligion = () => {
    if (religionUpdateType === "student") {
      showConfirmDialog(
        "Confirm Update",
        `Are you sure you want to update religious classification for student ${religionExamNo} to ${religionClassification === "1" ? "Christian (CRS)" : "Muslim (IRS)"}?`,
        handleUpdateReligionForStudent
      );
    } else {
      showConfirmDialog(
        "Confirm Update",
        `Are you sure you want to update religious classification for school ${religionSchoolCode} to ${religionClassification === "1" ? "Christian (CRS)" : "Muslim (IRS)"}?`,
        handleUpdateReligionForSchool
      );
    }
  };

  // --- BULK DOWNLOAD API LOGIC ---

  // Helper function to get image data for PDF
  const getImageDataForPDF = async (imagePath: string): Promise<string | null> => {
    if (!imagePath) return null;
    if (imagePath.startsWith("data:")) return imagePath;
    const url = `${baseUrl()}assets${imagePath}`;
    try {
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }
    } catch (error) {
      console.error("Fetch fallback error:", error);
    }
    return null;
  };

  const getLgaNameByCode = (codeOrStudentNum: string): string => {
    if (!codeOrStudentNum) return "N/A";
    const cleanStr = String(codeOrStudentNum).trim();
    const prefix = cleanStr.substring(0, 2);
    const id = parseInt(prefix, 10);
    const localGovernments = [
      { id: 1, name: "Aniocha North" }, { id: 2, name: "Aniocha South" }, { id: 3, name: "Bomadi" },
      { id: 4, name: "Burutu" }, { id: 5, name: "Ethiope East" }, { id: 6, name: "Ethiope West" },
      { id: 7, name: "Ika North East" }, { id: 8, name: "Ika South" }, { id: 9, name: "Isoko North" },
      { id: 10, name: "Isoko South" }, { id: 11, name: "Ndokwa East" }, { id: 12, name: "Ndokwa West" },
      { id: 13, name: "Okpe" }, { id: 14, name: "Oshimili North" }, { id: 15, name: "Oshimili South" },
      { id: 16, name: "Patani" }, { id: 17, name: "Sapele" }, { id: 18, name: "Udu" },
      { id: 19, name: "Ughelli North" }, { id: 20, name: "Ughelli South" }, { id: 21, name: "Ukwuani" },
      { id: 22, name: "Uvwie" }, { id: 23, name: "Warri North" }, { id: 24, name: "Warri South" },
      { id: 25, name: "Warri South West" },
    ];
    const found = localGovernments.find(lga => lga.id === id);
    return found ? found.name : cleanStr;
  };

  const generateSinglePDF = async (doc: any, resultData: any, logoDataUrl: string, isFirstPage: boolean, includePassport: boolean = true, includeDOB: boolean = true, qrDataUrl: string = "") => {
    if (!isFirstPage) {
      doc.addPage([210, 240]); // must specify custom size for each new page
    }
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const currentPrintDate = dayjs().format("DD-MM-YYYY HH:mm");

    const rawStudentInfo = resultData.studentInfo || {};
    const studentInfo = {
      studentNumber: rawStudentInfo.studentNumber || "N/A",
      firstname: rawStudentInfo.firstname || "",
      lastname: rawStudentInfo.lastname || "",
      othername: rawStudentInfo.othername || "",
      gender: rawStudentInfo.gender || "N/A",
      dateOfBirth: rawStudentInfo.dateOfBirth || null,
      passport: rawStudentInfo.passport || "",
      passportBase64: rawStudentInfo.passportBase64 || "",
      schoolName: rawStudentInfo.schoolName || "N/A",
      lgaName: getLgaNameByCode(rawStudentInfo.studentNumber || "17"),
      year: rawStudentInfo.year || "N/A",
      remark: resultData.remark || "N/A",
      accCode: resultData.accCode || "N/A"
    };

    const scoresData = resultData.scores || {};
    const religiousType = String(scoresData.religious?.type || "");
    const religiousSubjectName = religiousType === "2"
      ? "ISLAMIC RELIGIOUS STUDIES"
      : (religiousType === "1" ? "CHRISTIAN RELIGIOUS STUDIES" : "RELIGIOUS STUDIES");

    const scores = [
      { subject: "ENGLISH STUDIES", grade: scoresData.english?.grade || "-" },
      { subject: "MATHEMATICS", grade: scoresData.arithmetic?.grade || "-" },
      { subject: "GENERAL PAPER", grade: scoresData.generalPaper?.grade || "-" },
      { subject: religiousSubjectName, grade: scoresData.religious?.grade || "-" }
    ];

    doc.setLineWidth(0.6);
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("FEDERAL REPUBLIC OF NIGERIA", pageWidth / 2, 17, { align: "center" });
    doc.text("FEDERAL REPUBLIC OF NIGERIA", (pageWidth / 2) + 0.15, 17, { align: "center" });

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', (pageWidth / 2) - 10, 21, 20, 20);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("MINISTRY OF PRIMARY EDUCATION, ASABA", pageWidth / 2, 49, { align: "center" });
    doc.text("MINISTRY OF PRIMARY EDUCATION, ASABA", (pageWidth / 2) + 0.15, 49, { align: "center" });
    doc.text("DELTA STATE", pageWidth / 2, 55, { align: "center" });
    doc.text("DELTA STATE", (pageWidth / 2) + 0.15, 55, { align: "center" });

    doc.setFontSize(14);
    doc.text("COGNITIVE/PLACEMENT CERTIFICATION RESULT", pageWidth / 2, 63, { align: "center" });
    doc.text("COGNITIVE/PLACEMENT CERTIFICATION RESULT", (pageWidth / 2) + 0.15, 63, { align: "center" });
    doc.setFontSize(11);
    doc.text(`SESSION: ${studentInfo.year}`, pageWidth / 2, 69, { align: "center" });

    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`DATE PRINTED: ${currentPrintDate}`, 190, 72, { align: "right" });

    doc.setFontSize(10.5);
    let pY = 82;
    const labelX = 25;
    const valueX = 65;

    const addDetailLine = (label: string, value: string, boldVal: boolean = false) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, labelX, pY);
      doc.text(`${label}:`, labelX + 0.15, pY);
      doc.setFont("helvetica", boldVal ? "bold" : "normal");
      const lines = doc.splitTextToSize(value || "N/A", 120);
      doc.text(lines, valueX, pY);
      doc.text(lines, valueX + 0.15, pY);
      pY += (lines.length * 6);
    };

    addDetailLine("LGA", studentInfo.lgaName);
    addDetailLine("SCHOOL", studentInfo.schoolName);
    addDetailLine("CANDIDATE NAME", `${studentInfo.lastname.toUpperCase()} ${studentInfo.firstname.toUpperCase()} ${studentInfo.othername || ""}`);
    addDetailLine("EXAMINATION NO", studentInfo.studentNumber);
    const formatDOB = (dob: any) => {
      if (!dob) return "N/A";
      const strDob = String(dob).trim();
      const parsed = dayjs(strDob, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"]);
      return parsed.isValid() ? parsed.format("DD-MM-YYYY") : strDob;
    };

    if (includeDOB) {
      addDetailLine("DATE OF BIRTH", formatDOB(studentInfo.dateOfBirth));
    }
    addDetailLine("SEX", studentInfo.gender.toUpperCase());
    addDetailLine("ACCESS PIN", studentInfo.accCode);

    if (includePassport && studentInfo.passport) {
      const isPng = studentInfo.passport.toUpperCase().endsWith(".PNG");
      let portraitData = await getImageDataForPDF(studentInfo.passport);
      if (!portraitData && studentInfo.passportBase64) {
        portraitData = studentInfo.passportBase64;
      }
      if (portraitData) {
        const fmt = portraitData.includes("image/png") || isPng ? "PNG" : "JPEG";
        doc.addImage(portraitData, fmt, 165, 81, 20, 24);
      } else if (baseUrl().includes("http")) {
        try {
          const proxyUrl = `https://corsproxy.io/?url=${encodeURIComponent(`${baseUrl()}assets${studentInfo.passport}`)}`;
          const response = await fetch(proxyUrl);
          if (response.ok) {
            const blob = await response.blob();
            if (blob.type.includes("image")) {
              const dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              const fmt2 = dataUrl.includes("image/png") || isPng ? "PNG" : "JPEG";
              doc.addImage(dataUrl, fmt2, 165, 81, 20, 24);
            }
          }
        } catch (e) {
          console.warn("Proxy failed", e);
        }
      }
    }

    doc.setLineWidth(0.3);
    doc.rect(20, 76, 170, pY - 73);

    autoTable(doc, {
      startY: pY + 5,
      head: [["SUBJECT", "GRADE"]],
      body: scores.map((s: any) => [s.subject.toUpperCase(), s.grade]),
      headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontSize: 12, fontStyle: 'bold', lineWidth: 0.2, lineColor: [0, 0, 0] },
      columnStyles: { 0: { cellWidth: 145, halign: 'left' }, 1: { cellWidth: 25, halign: 'center' } },
      styles: { fontSize: 10.5, fontStyle: 'bold', cellPadding: 3, textColor: [0, 0, 0], font: "helvetica", lineWidth: 0.2, lineColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      margin: { left: 20, right: 20 },
      theme: 'grid'
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`REMARK: ${studentInfo.remark.toUpperCase()}`, 20, finalY);
    doc.text(`REMARK: ${studentInfo.remark.toUpperCase()}`, 20.15, finalY);

    if (qrDataUrl) {
      try {
        doc.addImage(qrDataUrl, 'PNG', 165, finalY - 5, 25, 25);
      } catch (e) {
        doc.rect(165, finalY - 5, 25, 25);
      }
    } else {
      doc.rect(165, finalY - 5, 25, 25);
    }
    doc.setFontSize(5);
    doc.text("VERIFIED", 177.5, finalY + 22, { align: "center" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("DELTA STATE MINISTRY OF PRIMARY EDUCATION", pageWidth / 2, pageHeight - 15, { align: "center" });
    doc.text("DELTA STATE MINISTRY OF PRIMARY EDUCATION", (pageWidth / 2) + 0.15, pageHeight - 15, { align: "center" });

  };

  const handleBulkDownload = async (includePassport: boolean = true, includeDOB: boolean = true) => {
    if (!bulkLgaCode || !bulkSchoolCode) {
      showSnackbar("Please provide LGA Code and School Code", 'warning');
      return;
    }

    setBulkLoading(true);
    setBulkProgress({ current: 0, total: 0 });

    try {
      const res = await axios.post(`${baseUrl()}results/bulkBySchool`, {
        lgaCode: String(bulkLgaCode),
        schoolCode: String(bulkSchoolCode)
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.data.status === true && res.data.data && Array.isArray(res.data.data)) {
        const students = res.data.data;
        if (students.length === 0) {
          showSnackbar("No students found for this school.", 'warning');
          setBulkLoading(false);
          return;
        }

        setBulkProgress({ current: 0, total: students.length });

        // Pre-load logo to avoid doing it for every student
        const logoCanvas = document.createElement("canvas");
        let logoDataUrl = "";
        try {
          const img = new Image();
          img.src = DeltaLogo;
          await new Promise((resolve) => {
            img.onload = () => {
              const size = Math.min(img.naturalWidth, img.naturalHeight);
              logoCanvas.width = size;
              logoCanvas.height = size;
              const ctx = logoCanvas.getContext("2d");
              if (ctx) {
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.clip();
                const sx = (img.naturalWidth - size) / 2;
                const sy = (img.naturalHeight - size) / 2;
                ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
                logoDataUrl = logoCanvas.toDataURL("image/png");
              }
              resolve(true);
            };
            img.onerror = () => resolve(false);
          });
        } catch (e) {
          console.warn("Could not preload logo", e);
        }

        // Create a single PDF document for the bulk download
        const doc = new jsPDF({ format: [210, 240] });
        let successCount = 0;

        // Loop over students and generate PDF pages
        for (let i = 0; i < students.length; i++) {
          const student = students[i];
          setBulkProgress({ current: i + 1, total: students.length });

          try {
            // Update QR value and give React time to render the hidden canvas
            setQrValue(`https://deltamobse.ng/verify/${student.studentNumber}`);
            await new Promise(resolve => setTimeout(resolve, 100)); // wait for render

            // Generate QR code data URL from the hidden canvas
            let qrDataUrl = "";
            if (qrCanvasRef.current) {
              qrDataUrl = qrCanvasRef.current.toDataURL("image/png");
            }
            await generateSinglePDF(doc, student, logoDataUrl, i === 0, includePassport, includeDOB, qrDataUrl);
            successCount++;
          } catch (pdfErr) {
            console.error(`PDF generation failed for student ${i + 1}:`, pdfErr);
          }

          // Slight delay to prevent freezing
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Save the compiled PDF — even if some pages had errors
        if (successCount > 0) {
          const suffix = includePassport ? '' : '_NoPassport';
          doc.save(`Delta_Bulk_Results_${bulkLgaCode}_${bulkSchoolCode}${suffix}.pdf`);
          showSnackbar(`Downloaded ${successCount} of ${students.length} results`, 'success');
        } else {
          showSnackbar(`PDF generation failed for all students. Check console for errors.`, 'error');
        }
      } else {
        showSnackbar(res.data.message || "Failed to retrieve bulk results", 'error');
      }
    } catch (e: any) {
      if (e.response && e.response.status === 404) {
        showSnackbar("No students found.", 'error');
      } else if (e.response && e.response.status === 403) {
        showSnackbar("Blocked school or unauthorized.", 'error');
      } else {
        showSnackbar("Error connecting to server", 'error');
      }
      console.error(e);
    } finally {
      setBulkLoading(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkDownloadLGA = async (includePassport: boolean = true, includeDOB: boolean = true) => {
    if (!bulkLgaOnlyCode) {
      showSnackbar("Please provide LGA Code", 'warning');
      return;
    }

    setBulkLgaLoading(true);
    setBulkLgaProgress({ currentSchool: 0, totalSchools: 0, currentStudent: 0, totalStudents: 0 });

    try {
      const endpoint = includePassport ? "results/bulkByLgaPhoto" : "results/bulkByLga";
      const res = await axios.post(`${baseUrl()}${endpoint}`, {
        lgaCode: String(bulkLgaOnlyCode)
      }, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.data.status === true && res.data.data && Array.isArray(res.data.data)) {
        const students = res.data.data;
        if (students.length === 0) {
          showSnackbar("No students found for this LGA.", 'warning');
          setBulkLgaLoading(false);
          return;
        }

        // Group students by school
        const studentsBySchool: Record<string, any[]> = {};
        for (const student of students) {
          const rawStudentInfo = student.studentInfo || student || {};
          let schoolName = rawStudentInfo.schoolName || student.SCHOOLNAME || student.schoolName || student.SCHOOLCODE || student.institutionCd;

          if (!schoolName || schoolName === "N/A") {
            const sn = String(rawStudentInfo.studentNumber || student.EXAMINATIONNO || student.examinationNo || "");
            if (sn.length >= 6) {
              schoolName = `School_${sn.substring(0, sn.length - 3)}`;
            } else {
              schoolName = "Unknown_School";
            }
          }

          if (!studentsBySchool[schoolName]) {
            studentsBySchool[schoolName] = [];
          }
          studentsBySchool[schoolName].push(student);
        }

        const schoolNames = Object.keys(studentsBySchool);
        setBulkLgaProgress({ currentSchool: 0, totalSchools: schoolNames.length, currentStudent: 0, totalStudents: students.length });

        // Pre-load logo to avoid doing it for every student
        const logoCanvas = document.createElement("canvas");
        let logoDataUrl = "";
        try {
          const img = new Image();
          img.src = DeltaLogo;
          await new Promise((resolve) => {
            img.onload = () => {
              const size = Math.min(img.naturalWidth, img.naturalHeight);
              logoCanvas.width = size;
              logoCanvas.height = size;
              const ctx = logoCanvas.getContext("2d");
              if (ctx) {
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.clip();
                const sx = (img.naturalWidth - size) / 2;
                const sy = (img.naturalHeight - size) / 2;
                ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
                logoDataUrl = logoCanvas.toDataURL("image/png");
              }
              resolve(true);
            };
            img.onerror = () => resolve(false);
          });
        } catch (e) {
          console.warn("Could not preload logo", e);
        }

        let successCountSchools = 0;
        let totalProcessedStudents = 0;

        for (let s = 0; s < schoolNames.length; s++) {
          const schoolName = schoolNames[s];
          const schoolStudents = studentsBySchool[schoolName];

          // Create a new PDF document for this school
          const doc = new jsPDF({ format: [210, 240] });
          let successCountStudents = 0;

          for (let i = 0; i < schoolStudents.length; i++) {
            const student = schoolStudents[i];
            totalProcessedStudents++;
            setBulkLgaProgress({
              currentSchool: s + 1,
              totalSchools: schoolNames.length,
              currentStudent: totalProcessedStudents,
              totalStudents: students.length
            });

            try {
              // Update QR value and give React time to render the hidden canvas
              setQrValue(`https://deltamobse.ng/verify/${student.studentInfo?.studentNumber}`);
              await new Promise(resolve => setTimeout(resolve, 100)); // wait for render

              // Generate QR code data URL from the hidden canvas
              let qrDataUrl = "";
              if (qrCanvasRef.current) {
                qrDataUrl = qrCanvasRef.current.toDataURL("image/png");
              }
              await generateSinglePDF(doc, student, logoDataUrl, i === 0, includePassport, includeDOB, qrDataUrl);
              successCountStudents++;
            } catch (pdfErr) {
              console.error(`PDF generation failed for student ${i + 1} in school ${schoolName}:`, pdfErr);
            }
          }

          if (successCountStudents > 0) {
            const suffix = includePassport ? '' : '_NoPassport';
            const safeSchoolName = schoolName.replace(/[^a-z0-9]/gi, '_');
            doc.save(`Delta_Bulk_Results_${bulkLgaOnlyCode}_${safeSchoolName}${suffix}.pdf`);
            successCountSchools++;
          }

          // Slight delay between schools to prevent freezing and allow downloads to start properly
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        if (successCountSchools > 0) {
          showSnackbar(`Downloaded ${successCountSchools} school PDFs successfully`, 'success');
        } else {
          showSnackbar(`PDF generation failed. Check console for errors.`, 'error');
        }
      } else {
        showSnackbar(res.data.message || "Failed to retrieve bulk LGA results", 'error');
      }
    } catch (e: any) {
      if (e.response && e.response.status === 404) {
        showSnackbar("No students found.", 'error');
      } else if (e.response && e.response.status === 403) {
        showSnackbar("Blocked LGA or unauthorized.", 'error');
      } else {
        showSnackbar("Error connecting to server", 'error');
      }
      console.error(e);
    } finally {
      setBulkLgaLoading(false);
      setBulkLgaProgress({ currentSchool: 0, totalSchools: 0, currentStudent: 0, totalStudents: 0 });
    }
  };

  // --- GLOBAL RESULT RELEASE ---
  const handleGlobalRelease = async () => {
    setLoading(true);
    try {
      localStorage.setItem('globalReleaseStatus', 'true');
      setIsReleased(true);

      try {
        await axios.post(`${baseUrl()}Admin2s/globalRelease`, {});
      } catch (err) {
        console.error("Backend release API error:", err);
      }

      showSnackbar("All results released successfully", 'success');
    } catch (e) {
      showSnackbar("An error occurred locally", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalUnrelease = async () => {
    setLoading(true);
    try {
      localStorage.setItem('globalReleaseStatus', 'false');
      setIsReleased(false);

      try {
        await axios.post(`${baseUrl()}Admin2s/globalUnrelease`, {});
      } catch (err) {
        console.error("Backend unrelease API error:", err);
      }

      showSnackbar("All results unreleased successfully", 'success');
    } catch (e) {
      showSnackbar("An error occurred locally", 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (menu: string) => {
    history.push("/" + menu);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HIDDEN QR CODE CANVAS FOR PDF GENERATION */}
            <div style={{ position: "absolute", top: "-9999px", visibility: "hidden" }}>
              <QRCodeCanvas ref={qrCanvasRef} value={qrValue} size={65} level="H" />
            </div>

            {/* HEADER */}
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Management Console
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </Header>


            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              <Button
                onClick={() => history.goBack()}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="primary">Management Console</Typography>
                <Typography color="text.secondary">Count, delete, and block results by LGA, school, or student</Typography>
              </Box>

              {/* 1. COUNT RESULTS */}
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Count Results</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View total results with optional filtering by LGA and School
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="LGA ID"
                      size="small"
                      value={countLgaId}
                      onChange={(e) => setCountLgaId(e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="School Code"
                      size="small"
                      value={countSchoolCode}
                      onChange={(e) => setCountSchoolCode(e.target.value)}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Calculate />}
                      onClick={handleCountResults}
                      sx={{ bgcolor: "#243b5a" }}
                    >
                      Count Results
                    </Button>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h2" fontWeight={900} color="primary">{count}</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">Total Results</Typography>
                  <Typography variant="caption" color="text.secondary">All student examination results in database</Typography>
                </Box>
              </Paper>

              {/* 1.5. CHECKED RESULTS COUNT */}
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Checked Results Count</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View total number of students who have checked their result
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Calculate />}
                      onClick={handleGetCheckedCount}
                      disabled={checkedCountLoading}
                      sx={{ bgcolor: "#243b5a" }}
                    >
                      {checkedCountLoading ? "Fetching..." : "Get Checked Count"}
                    </Button>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h2" fontWeight={900} color="primary">{checkedCount}</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">Total Checked</Typography>
                  <Typography variant="caption" color="text.secondary">Students who successfully viewed their result</Typography>
                </Box>
              </Paper>

              {/* 2. TABS SECTION */}
              <Paper sx={{ mb: 4, borderRadius: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="fullWidth"
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{
                    '& .MuiTab-root': { fontWeight: 600 },
                  }}
                >
                  <Tab label="Delete Operations" />
                  <Tab label="Block/Unblock" />
                  <Tab label="Religious Class" />
                  <Tab label="Bulk Download" />
                </Tabs>

                <Box sx={{ p: 4 }}>
                  {/* TAB 0: DELETE */}
                  {activeTab === 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By Student (Single Result)</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={deleteLgaId}
                          onChange={(e) => setDeleteLgaId(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="School Code"
                          value={deleteSchoolCode}
                          onChange={(e) => setDeleteSchoolCode(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Exam Number"
                          value={deleteExamNo}
                          onChange={(e) => setDeleteExamNo(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteForever />}
                          onClick={() => showConfirmDialog(
                            "Confirm Delete",
                            `Are you sure you want to delete result for student ${deleteExamNo}?`,
                            handleDeleteSingleResult
                          )}
                        >
                          Delete Student Result
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By School (All Results)</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={deleteLgaId}
                          onChange={(e) => setDeleteLgaId(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="School Code"
                          value={deleteSchoolCode}
                          onChange={(e) => setDeleteSchoolCode(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteForever />}
                          onClick={() => showConfirmDialog(
                            "Confirm Delete",
                            `Are you sure you want to delete ALL results for school ${deleteSchoolCode}?`,
                            handleDeleteSchoolResults
                          )}
                        >
                          Delete All School Results
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By LGA (All Results)</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={deleteLgaId}
                          onChange={(e) => setDeleteLgaId(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteForever />}
                          onClick={() => showConfirmDialog(
                            "CRITICAL: Confirm Delete",
                            `Are you absolutely sure you want to delete ALL results for LGA ${deleteLgaId}? This action cannot be undone.`,
                            handleDeleteLGAResults
                          )}
                        >
                          Delete All LGA Results
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {/* TAB 1: BLOCK / UNBLOCK */}
                  {activeTab === 1 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By Student</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={blockLgaId}
                          onChange={(e) => setBlockLgaId(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="School Code"
                          value={blockSchoolCode}
                          onChange={(e) => setBlockSchoolCode(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Exam Number"
                          value={blockExamNo}
                          onChange={(e) => setBlockExamNo(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            startIcon={<Block />}
                            onClick={() => showConfirmDialog(
                              "Confirm Block",
                              `Are you sure you want to BLOCK result for student ${blockExamNo}?`,
                              handleBlockStudent
                            )}
                          >
                            Block
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<LockOpen />}
                            onClick={() => showConfirmDialog(
                              "Confirm Unblock",
                              `Are you sure you want to UNBLOCK result for student ${blockExamNo}?`,
                              handleUnblockStudent
                            )}
                          >
                            Unblock
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By School</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={blockLgaId}
                          onChange={(e) => setBlockLgaId(e.target.value)}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="School Code"
                          value={blockSchoolCode}
                          onChange={(e) => setBlockSchoolCode(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            startIcon={<Block />}
                            onClick={() => showConfirmDialog(
                              "Confirm Block",
                              `Are you sure you want to BLOCK ALL results for school ${blockSchoolCode}?`,
                              handleBlockSchool
                            )}
                          >
                            Block All
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<LockOpen />}
                            onClick={() => showConfirmDialog(
                              "Confirm Unblock",
                              `Are you sure you want to UNBLOCK ALL results for school ${blockSchoolCode}?`,
                              handleUnblockSchool
                            )}
                          >
                            Unblock All
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>By LGA</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="LGA ID"
                          value={blockLgaId}
                          onChange={(e) => setBlockLgaId(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            startIcon={<Block />}
                            onClick={() => showConfirmDialog(
                              "Confirm Block",
                              `Are you sure you want to BLOCK ALL results for LGA ${blockLgaId}?`,
                              handleBlockLGA
                            )}
                          >
                            Block All
                          </Button>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<LockOpen />}
                            onClick={() => showConfirmDialog(
                              "Confirm Unblock",
                              `Are you sure you want to UNBLOCK ALL results for LGA ${blockLgaId}?`,
                              handleUnblockLGA
                            )}
                          >
                            Unblock All
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {/* TAB 2: RELIGION - UPDATED */}
                  {activeTab === 2 && (
                    <Box>
                      {/* Update Type Selection */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Update Type
                        </Typography>
                        <RadioGroup
                          row
                          value={religionUpdateType}
                          onChange={(e) => setReligionUpdateType(e.target.value as "student" | "school")}
                        >
                          <FormControlLabel
                            value="school"
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Business fontSize="small" />
                                <span>Update School (All Students)</span>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="student"
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person fontSize="small" />
                                <span>Update Single Student</span>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </Box>

                      <Grid container spacing={3}>
                        {/* Common Fields */}
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            LGA ID
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter LGA ID"
                            value={religionLgaId}
                            onChange={(e) => setReligionLgaId(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            School Code
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter School Code"
                            value={religionSchoolCode}
                            onChange={(e) => setReligionSchoolCode(e.target.value)}
                          />
                        </Grid>

                        {/* Student-specific field */}
                        {religionUpdateType === "student" && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Exam Number
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter Exam Number"
                              value={religionExamNo}
                              onChange={(e) => setReligionExamNo(e.target.value)}
                            />
                          </Grid>
                        )}

                        {/* Classification Selection */}
                        <Grid item xs={12} md={religionUpdateType === "student" ? 12 : 4}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Religious Classification
                          </Typography>
                          <FormControl fullWidth size="small">
                            <InputLabel>Select Classification</InputLabel>
                            <Select
                              value={religionClassification}
                              label="Select Classification"
                              onChange={(e) => setReligionClassification(e.target.value)}
                            >
                              <MenuItem value="1">Christian (CRS)</MenuItem>
                              <MenuItem value="2">Muslim (IRS)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Update Button */}
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleUpdateReligion}
                            sx={{
                              bgcolor: "#243b5a",
                              py: 1.5,
                              mt: 2
                            }}
                          >
                            {religionUpdateType === "student"
                              ? `Update Student to ${religionClassification === "1" ? "Christian (CRS)" : "Muslim (IRS)"}`
                              : `Update School to ${religionClassification === "1" ? "Christian (CRS)" : "Muslim (IRS)"}`
                            }
                          </Button>
                        </Grid>
                      </Grid>

                      {/* Info Box */}
                      <Box sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: '#e0f2fe',
                        borderRadius: 2,
                        borderLeft: '4px solid #0284c7'
                      }}>
                        <Typography variant="caption" color="info.main" fontWeight="bold">
                          ℹ️ Information
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {religionUpdateType === "student"
                            ? "Updating a student will modify the religious classification for both their registration and examination results."
                            : "Updating a school will modify the religious classification for ALL students in that school, affecting both registrations and examination results."
                          }
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* TAB 3: BULK DOWNLOAD */}
                  {activeTab === 3 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Bulk Download School Results
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="LGA Code"
                            placeholder="Enter LGA Code"
                            value={bulkLgaCode}
                            onChange={(e) => setBulkLgaCode(e.target.value)}
                            disabled={bulkLoading}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="School Code"
                            placeholder="Enter School Code"
                            value={bulkSchoolCode}
                            onChange={(e) => setBulkSchoolCode(e.target.value)}
                            disabled={bulkLoading}
                          />
                        </Grid>

                        {/* <Grid item xs={12} md={2}>
                          <Button 
                            fullWidth 
                            variant="contained" 
                            startIcon={<Download />} 
                            onClick={() => handleBulkDownload(true)}
                            disabled={bulkLoading}
                            sx={{ 
                              bgcolor: "#243b5a",
                              height: "100%",
                              "&:disabled": { bgcolor: "#94a3b8" }
                            }}
                          >
                            {bulkLoading ? "Processing..." : "With Passport"}
                          </Button>
                        </Grid> */}
                        <Grid item xs={12} md={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Download />}
                            onClick={() => handleBulkDownload(false, true)}
                            disabled={bulkLoading}
                            sx={{
                              bgcolor: "#243b5a",
                              color: "#ffffff",
                              height: "100%",
                              "&:disabled": { bgcolor: "#94a3b8" }
                            }}
                          >
                            {bulkLoading ? "Processing..." : "Download (With DOB)"}
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => handleBulkDownload(false, false)}
                            disabled={bulkLoading}
                            sx={{
                              borderColor: "#243b5a",
                              color: "#243b5a",
                              height: "100%",
                              "&:disabled": { borderColor: "#94a3b8", color: "#94a3b8" }
                            }}
                          >
                            {bulkLoading ? "Processing..." : "Download (No DOB)"}
                          </Button>
                        </Grid>
                      </Grid>

                      {bulkLoading && bulkProgress.total > 0 && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Downloading Result {bulkProgress.current} of {bulkProgress.total}...
                          </Typography>
                          <Box sx={{ width: '100%', mt: 2 }}>
                            <div style={{
                              width: '100%',
                              height: '10px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '5px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                                backgroundColor: '#243b5a',
                                transition: 'width 0.3s ease-in-out'
                              }} />
                            </div>
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ my: 4 }} />

                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Bulk Download LGA Results
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="LGA Code"
                            placeholder="Enter LGA Code"
                            value={bulkLgaOnlyCode}
                            onChange={(e) => setBulkLgaOnlyCode(e.target.value)}
                            disabled={bulkLgaLoading}
                          />
                        </Grid>

                        {/* <Grid item xs={12} md={2}>
                          <Button 
                            fullWidth 
                            variant="contained" 
                            startIcon={<Download />} 
                            onClick={() => handleBulkDownloadLGA(true)}
                            disabled={bulkLgaLoading}
                            sx={{ 
                              bgcolor: "#243b5a",
                              height: "100%",
                              "&:disabled": { bgcolor: "#94a3b8" }
                            }}
                          >
                            {bulkLgaLoading ? "Processing..." : "With Passport"}
                          </Button>
                        </Grid> */}
                        <Grid item xs={12} md={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Download />}
                            onClick={() => handleBulkDownloadLGA(false, true)}
                            disabled={bulkLgaLoading}
                            sx={{
                              bgcolor: "#243b5a",
                              color: "#ffffff",
                              height: "100%",
                              "&:disabled": { bgcolor: "#94a3b8" }
                            }}
                          >
                            {bulkLgaLoading ? "Processing..." : "Download (With DOB)"}
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => handleBulkDownloadLGA(false, false)}
                            disabled={bulkLgaLoading}
                            sx={{
                              borderColor: "#243b5a",
                              color: "#243b5a",
                              height: "100%",
                              "&:disabled": { borderColor: "#94a3b8", color: "#94a3b8" }
                            }}
                          >
                            {bulkLgaLoading ? "Processing..." : "Download (No DOB)"}
                          </Button>
                        </Grid>
                      </Grid>

                      {bulkLgaLoading && bulkLgaProgress.totalSchools > 0 && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Processing School {bulkLgaProgress.currentSchool} of {bulkLgaProgress.totalSchools} (Student {bulkLgaProgress.currentStudent} of {bulkLgaProgress.totalStudents})...
                          </Typography>
                          <Box sx={{ width: '100%', mt: 2 }}>
                            <div style={{
                              width: '100%',
                              height: '10px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '5px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${(bulkLgaProgress.currentSchool / bulkLgaProgress.totalSchools) * 100}%`,
                                backgroundColor: '#243b5a',
                                transition: 'width 0.3s ease-in-out'
                              }} />
                            </div>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* 3. GLOBAL RESULT RELEASE */}
              <Card elevation={4} sx={{ borderRadius: 3, border: '2px solid #e5e7eb' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>Global Result Release</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Control result access across all 25 LGAs at once. Released results are accessible to all users.
                  </Typography>

                  <Box sx={{ bgcolor: '#fff3e0', p: 3, borderRadius: 2, mb: 3, borderLeft: '5px solid #f59e0b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Public sx={{ color: '#f59e0b' }} /> Global Controls
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use these controls to release or unrelease results for all LGAs simultaneously. This affects result visibility across the entire system.
                      </Typography>
                    </Box>
                    <Box>
                      <Chip
                        label={isReleased ? "CURRENTLY RELEASED" : "CURRENTLY UNRELEASED"}
                        color={isReleased ? "success" : "error"}
                        sx={{ fontWeight: 'bold', fontSize: '14px', py: 2.5, px: 1 }}
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        sx={{ py: 2, fontWeight: 'bold' }}
                        onClick={() => showConfirmDialog(
                          "Confirm Global Release",
                          "Are you sure you want to RELEASE ALL results? This will make all results visible to students across all LGAs.",
                          handleGlobalRelease
                        )}
                      >
                        Release All Results
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        sx={{ py: 2, fontWeight: 'bold' }}
                        onClick={() => showConfirmDialog(
                          "Confirm Global Unrelease",
                          "Are you sure you want to HIDE ALL results? This will hide all results from students across all LGAs.",
                          handleGlobalUnrelease
                        )}
                      >
                        Unrelease All Results
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </Container>

            {/* FOOTER */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: '100%', boxShadow: 3 }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              {confirmDialog.title}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="confirm-dialog-description">
                {confirmDialog.message}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmDialog} color="primary">
                Cancel
              </Button>
              <Button onClick={handleConfirmAction} color="error" variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default ManagementConsole;